import { ref, watch } from 'vue'
import apiService from '@/apiService'

/**
 * Composable for fetching and managing album-specific changelog data
 * @param {Ref<number>} albumId - Reactive album ID to fetch changelog for
 * @returns {Object} - Changelog state and methods
 */
export function useAlbumChangelog(albumId) {
  const changelog = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const pagination = ref({
    limit: 50,
    offset: 0,
    total: 0,
    hasMore: false
  })

  /**
   * Fetch changelog entries for the album
   * @param {Object} params - Query parameters (limit, offset)
   */
  const fetchChangelog = async (params = {}) => {
    if (!albumId.value) {
      error.value = 'Album ID is required'
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const queryParams = {
        limit: params.limit || pagination.value.limit,
        offset: params.offset || pagination.value.offset
      }

      const response = await apiService.getAlbumChangelog(albumId.value, queryParams)

      if (response && response.success) {
        const { data: responseData } = response

        // If offset is 0, replace changelog; otherwise append (pagination)
        if (queryParams.offset === 0) {
          changelog.value = responseData.changelog || []
        } else {
          changelog.value = [...changelog.value, ...(responseData.changelog || [])]
        }

        // Update pagination info
        if (response.pagination) {
          pagination.value = response.pagination
        }
      } else {
        throw new Error('Invalid response format')
      }
    } catch (err) {
      console.error('Changelog fetch error:', err)

      // Handle different error types
      if (err.response) {
        const status = err.response.status
        if (status === 404) {
          error.value = 'Album not found'
        } else if (status === 401) {
          error.value = 'Authentication required'
        } else if (status === 400) {
          error.value = 'Invalid album ID'
        } else {
          error.value = err.response.data?.message || 'Failed to load changelog'
        }
      } else if (err.request) {
        error.value = 'Network error - please check your connection'
      } else {
        error.value = err.message || 'Failed to load changelog'
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Retry fetching changelog (resets to first page)
   */
  const retry = () => {
    pagination.value.offset = 0
    changelog.value = []
    fetchChangelog()
  }

  /**
   * Load more changelog entries (pagination)
   */
  const loadMore = () => {
    if (pagination.value.hasMore && !isLoading.value) {
      fetchChangelog({
        offset: pagination.value.offset + pagination.value.limit
      })
    }
  }

  /**
   * Reset changelog state
   */
  const reset = () => {
    changelog.value = []
    error.value = null
    isLoading.value = false
    pagination.value = {
      limit: 50,
      offset: 0,
      total: 0,
      hasMore: false
    }
  }

  // Watch albumId changes and refetch
  watch(albumId, (newId, oldId) => {
    if (newId && newId !== oldId) {
      reset()
      fetchChangelog()
    }
  })

  return {
    changelog,
    isLoading,
    error,
    pagination,
    fetchChangelog,
    retry,
    loadMore,
    reset
  }
}
